import { DateRange as DateRangeIcon, PictureAsPdf } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { format } from 'date-fns'
import React, { useCallback, useMemo, useState } from 'react'
import { useGetList, useGetOne, useTranslate } from 'react-admin'
import { trackNow } from '../analytics/usageTracker'
import { prepareGlucoseChartSeries } from '../common'
import { stableFallbackStartMs, stableWindowEndMs } from '../dashboard/components/petDetailWindow'
import { useDeviceMetrics } from '../hooks/useDeviceMetrics'
import { useEarliestMetricDate } from '../hooks/useEarliestMetricDate'
import { useExportSelection } from '../hooks/useExportSelection'
import { useGlucoseDisplay } from '../hooks/useGlucoseDisplay'
import { usePetTargetRange } from '../hooks/usePetTargetRange'
import { isValidDate } from '../utils/dateUtils'
import { formatTrendSeriesTitle } from '../utils/deviceWearPeriods'
import { SHARED_METRICS_PER_PAGE } from '../utils/paginationUtils'
import { countMaskedReadings, recordExportEvent } from '../utils/recordExportEvent'
import type { VetReportEmrNote } from '../utils/veterinaryPDFExport'
import { ReportDeviceSelector } from './ReportDeviceSelector'
// veterinaryPDFExport is dynamically imported in handleExportInternal to avoid loading on startup

/**
 * Data Coverage for the PDF, computed over the actual data-bearing window
 * [first reading → last reading] at the same 3-minute cadence the dashboard
 * uses (tirStats.ts). This is INDEPENDENT of the selected report range, so the
 * PDF never disagrees with the dashboard just because the picked range padded
 * the denominator with days the sensor was never worn (e.g. "Last 7 days" on a
 * 2-day device previously read ~29% here vs ~100% on the dashboard).
 *
 * @param readingTimestampsMs timestamps (ms) of the readings actually present
 * @param readingCount number of readings used for the stats
 */
export function computeReadingCoverage(
  readingTimestampsMs: number[],
  readingCount: number,
): number {
  if (readingTimestampsMs.length < 2) return 100
  const dataSpanMs = Math.max(...readingTimestampsMs) - Math.min(...readingTimestampsMs)
  const expectedReadings = Math.floor(dataSpanMs / (3 * 60 * 1000))
  return expectedReadings > 0 ? Math.min(100, (readingCount / expectedReadings) * 100) : 100
}

interface GlucosePDFExportProps {
  petId: string | number
  deviceId: string | number
  startDate?: Date
  endDate?: Date
  variant?: 'button' | 'fab'
  size?: 'small' | 'medium' | 'large'
}

/** The EMR notes a report includes (includeInReport true or unset), in the exporter's shape. */
export const toReportEmrNotes = (emrNotes: any[]): VetReportEmrNote[] =>
  emrNotes
    .filter((n: any) => n.includeInReport !== false) // Include if true or undefined
    .map((n: any) => ({
      id: n.id,
      timestamp: n.timestamp,
      notes: n.notes,
      // `vet` is a users relationship: a populated object at depth 1, a bare id
      // otherwise. Print the NAME, never the object ("[object Object]", P74-2).
      vet: n.vet && typeof n.vet === 'object' ? n.vet.name || undefined : undefined,
    }))

export const GlucosePDFExport: React.FC<GlucosePDFExportProps> = ({
  petId,
  deviceId,
  startDate: startDateProp,
  endDate: endDateProp,
  variant = 'button',
  size = 'medium',
}) => {
  const translate = useTranslate()

  // S4 guard (same class as GlucoseCSVExport): the report bounds MUST be
  // render-stable. Per-render `new Date()` defaults plus ensureDate's "now"
  // fallback used to mint a NEW end bound whenever a date was missing or an
  // Invalid Date (which is truthy, so `value ? …` doesn't catch it — e.g.
  // actualDataRange built from a metric with no timestamp). Every render then
  // produced a fresh react-query key for the eager emrs/activities/metrics
  // queries below → new fetch → state update → re-render → new "now"… a
  // self-sustaining loop measured at 1,000-2,100 /api/emrs requests/minute in
  // prod (2026-07-09), with format(Invalid Date) crash-remount cycles feeding
  // it. Invalid/missing props converge on these two stable defaults instead.
  // Fallbacks are bucketed (stableWindowEndMs) so even REMOUNTS within the
  // same 5-minute window produce identical query keys → served from cache.
  const defaultStartDate = useMemo(
    () =>
      isValidDate(startDateProp) ? startDateProp : new Date(stableFallbackStartMs(Date.now())),
    [startDateProp],
  )
  const defaultEndDate = useMemo(
    () => (isValidDate(endDateProp) ? endDateProp : new Date(stableWindowEndMs(Date.now()))),
    [endDateProp],
  )
  const [isExporting, setIsExporting] = useState(false)
  const [exportStatus, setExportStatus] = useState<
    'idle' | 'preparing' | 'generating' | 'complete'
  >('idle')

  // Export range dialog state
  const [datePickerOpen, setDatePickerOpen] = useState(false)
  // Confirmation shown when the user generates over a range with no readings,
  // so they don't get a silently empty (all-zero) report.
  const [emptyConfirmOpen, setEmptyConfirmOpen] = useState(false)

  // What the report covers (vic/vetpo#56): the sensors the vet ticks (most
  // recent preselected), or a custom range clamped to the pet's data. Until the
  // dialog opens this is the page's own device + window, unchanged — so
  // nothing extra is fetched on page load. The bounds stay render-stable (the
  // S4 guard above): they come from state and fetched reading times, never
  // from a per-render `new Date()`. The old app-global calendarFilter* keys
  // stay removed (pass 13).
  const selection = useExportSelection({
    petId,
    deviceId,
    defaultStart: defaultStartDate,
    defaultEnd: defaultEndDate,
  })
  const effectiveStartDate = isValidDate(selection.start) ? selection.start : defaultStartDate
  const effectiveEndDate = isValidDate(selection.end) ? selection.end : defaultEndDate

  // Fetch pet information
  const { data: pet } = useGetOne('pets', { id: petId })

  // Effective TIR target range — the vet's per-pet override when set, else the
  // species default. This is the SAME resolution PetGlucoseDetailPanel uses, so
  // the PDF's Time in Range matches the on-screen report. (Using the
  // species-only hook here meant a vet who narrowed a patient's target saw one
  // TIR on screen and a different one in the PDF she handed the client.)
  const { targetMin, targetMax, highMax } = usePetTargetRange(petId)

  // Fetch activities for the report
  const {
    data: activities = [],
    isLoading: activitiesLoading,
    error: activitiesError,
  } = useGetList('activities', {
    // Explicit depth (L-089): REST default 2 embeds every relation per row; only ids are read.
    meta: { params: { depth: 0 } },
    filter: {
      pet: petId,
      start_time_gte: Math.floor(effectiveStartDate.getTime() / 1000),
      start_time_lte: Math.floor(effectiveEndDate.getTime() / 1000),
    },
    sort: { field: 'start_time', order: 'ASC' },
    pagination: { page: 1, perPage: 10000 },
  })

  // Fetch EMR notes for the report
  const {
    data: emrNotes = [],
    isLoading: emrNotesLoading,
    error: emrNotesError,
  } = useGetList('emrs', {
    // Explicit depth (L-089): REST default 2 embeds every relation per row; the vet NAME is shown.
    meta: { params: { depth: 1 } },
    filter: {
      pet: petId,
      timestamp_gte: effectiveStartDate.toISOString(),
      timestamp_lte: effectiveEndDate.toISOString(),
    },
    sort: { field: 'timestamp', order: 'ASC' },
    pagination: { page: 1, perPage: 10000 },
  })

  // Use the unified metrics hook that handles both single and virtual devices
  const {
    data: metrics = [],
    isLoading: metricsLoading,
    error: metricsError,
    isTruncated,
    truncatedBefore,
    perDeviceData,
  } = useDeviceMetrics({
    deviceId: selection.queryDeviceId,
    deviceIds: selection.queryDeviceIds,
    // One report trend chart per ticked sensor, each with its OWN readings
    // (overlap hours in both) — vic/vetpo#56.
    perDevice: selection.mode === 'devices',
    petId,
    startDate: effectiveStartDate,
    endDate: effectiveEndDate,
    sort: { field: 'timestamp', order: 'ASC' },
    pagination: { page: 1, perPage: SHARED_METRICS_PER_PAGE },
  })

  // Also fetch all metrics (without date filter) to determine actual data range.
  // Same perPage as GlucoseTrendChart's unwindowed fetch so the two DEVICE-WIDE
  // queries share one react-query entry instead of pulling the same rows twice.
  // `isLoading` matters as much as the data here: `allMetrics` defaults to []
  // while the query is in flight, which makes actualDataRange null and
  // selectedRangeHasData false — indistinguishable from a pet that genuinely
  // has no readings. Without this flag the confirm handler told vets "No data
  // in selected range" for pets with thousands of points (repro'd ~1 in 3 on
  // prod, pet 68238 / 8,707 points).
  const { data: allMetrics = [], isLoading: dataRangeLoading } = useDeviceMetrics({
    deviceId,
    petId,
    sort: { field: 'timestamp', order: 'ASC' },
    pagination: { page: 1, perPage: SHARED_METRICS_PER_PAGE },
  })

  // The true start of data, from a dedicated earliest-metric query (device
  // start_time floor, perPage 1 ASC) — immune to the metrics limit clamp.
  const { earliestDate } = useEarliestMetricDate(petId ?? null)

  // Calculate the actual data range. The unfiltered `allMetrics` fetch is
  // capped server-side and queried newest-first, so on long-history pets its
  // MINIMUM lies about the true data start (early ranges would be falsely
  // flagged "no data"); its maximum is reliable. Prefer the dedicated
  // earliest-metric query for the start.
  const actualDataRange = useMemo(() => {
    if (!allMetrics.length) return null

    // A metric with no timestamp would make min/max NaN → Invalid Dates that
    // poison selectedStart/EndDate via applyActualDataRange (see S4 guard).
    const timestamps = allMetrics.map((m) => m.timestamp * 1000).filter((t) => Number.isFinite(t))
    if (!timestamps.length) return null
    const cappedMin = Math.min(...timestamps)
    const maxTimestamp = Math.max(...timestamps)
    const startMs = earliestDate ? Math.min(earliestDate.getTime(), cappedMin) : cappedMin

    return {
      startDate: new Date(startMs),
      endDate: new Date(maxTimestamp),
    }
  }, [allMetrics, earliestDate])

  // The pet's data range: exact from the device list once the dialog loaded it,
  // else the unwindowed-query estimate.
  const dataRange = selection.petDataRange ?? actualDataRange

  // Check if selected date range has data
  const selectedRangeHasData = useMemo(() => {
    if (!dataRange) return false

    // Ranges overlap if selected doesn't end before data starts and doesn't start after data ends
    return (
      effectiveEndDate.getTime() >= dataRange.startDate.getTime() &&
      effectiveStartDate.getTime() <= dataRange.endDate.getTime()
    )
  }, [effectiveStartDate, effectiveEndDate, dataRange])

  // Get glucose unit preference from user store
  const { glucoseUnit, convertValue, formatValue } = useGlucoseDisplay()

  const correctedMetrics = useMemo(() => prepareGlucoseChartSeries(metrics), [metrics])

  // Calculate glucose statistics using corrected values (matching live dashboard)
  const glucoseStats = useMemo(() => {
    if (!correctedMetrics.length) return null

    const glucoseValues = correctedMetrics.map((m) => m.glucose)
    const avgGlucose = glucoseValues.reduce((a, b) => a + b, 0) / glucoseValues.length

    // Time in range — use the species target range (same as the dashboard and
    // the PDF's TIR table), NOT a hardcoded 3.9–10.0 window.
    const inRangeCount = glucoseValues.filter((g) => g >= targetMin && g <= targetMax).length
    const timeInRange = glucoseValues.length > 0 ? (inRangeCount / glucoseValues.length) * 100 : 0

    // Coefficient of Variation (CV)
    const stdDev = Math.sqrt(
      glucoseValues.reduce((sum, g) => sum + (g - avgGlucose) ** 2, 0) / glucoseValues.length,
    )
    const cv = avgGlucose > 0 ? (stdDev / avgGlucose) * 100 : 0

    const uniqueDays = new Set(
      correctedMetrics
        .filter((m) => typeof m.timestamp === 'number')
        .map((m) => new Date((m.timestamp as number) * 1000).toISOString().split('T')[0]),
    )

    // Data Coverage over the actual data-bearing window, matching the dashboard
    // (see computeReadingCoverage). NOT the full selected range, which can
    // include days with no data and would deflate coverage below the dashboard.
    const readingTimestamps = correctedMetrics
      .map((m) => (typeof m.timestamp === 'number' ? (m.timestamp as number) * 1000 : null))
      .filter((t): t is number => t !== null)
    const sensorCoverage = computeReadingCoverage(readingTimestamps, correctedMetrics.length)

    return {
      avgGlucose: formatValue(avgGlucose),
      timeInRange: timeInRange.toFixed(0),
      cv: cv.toFixed(0),
      stdDev: formatValue(stdDev),
      effectiveDays: uniqueDays.size,
      sensorCoverage: Math.min(100, sensorCoverage).toFixed(0),
    }
  }, [
    correctedMetrics,
    glucoseUnit,
    targetMin,
    targetMax,
    effectiveEndDate.getTime(),
    effectiveStartDate.getTime(),
  ])

  // Open the range dialog before export (loads the device list once)
  const { activate } = selection
  const handleOpenDatePicker = useCallback(() => {
    activate()
    setDatePickerOpen(true)
    trackNow({ type: 'report_opened', section: 'report_dialog', pet: Number(petId) })
  }, [activate, petId])

  // Handle date picker cancel
  const handleDatePickerCancel = useCallback(() => {
    setDatePickerOpen(false)
  }, [])

  // Apply actual data range (the empty-range guard's recovery)
  const { setMode, setCustomRange } = selection
  const applyActualDataRange = useCallback(() => {
    if (dataRange) {
      setMode('custom')
      setCustomRange(dataRange.startDate, dataRange.endDate)
    }
  }, [dataRange, setMode, setCustomRange])

  // Internal export function - defined before handleDatePickerConfirm which depends on it
  const handleExportInternal = useCallback(async () => {
    console.log('handleExportInternal called', { pet: !!pet, isExporting })
    if (!pet || isExporting) {
      console.log('Export skipped - pet:', !!pet, 'isExporting:', isExporting)
      return
    }

    setIsExporting(true)
    setExportStatus('preparing')
    console.log('Export started, preparing...')

    try {
      // Every chart in the report is drawn by the exporter from the data
      // passed in below (vic/vetpo#48) — nothing is captured from the page, so
      // there is no hidden chart to mount and no render wait.
      setExportStatus('generating')

      // Use new 6-page Veterinary CGM Report format
      const { VeterinaryPDFExporter } = await import('../utils/veterinaryPDFExport')
      const vetExporter = new VeterinaryPDFExporter()

      vetExporter.setMetrics(
        correctedMetrics
          .filter((m) => typeof m.timestamp === 'number')
          .map((m) => ({
            timestamp: m.timestamp as number,
            glucose: m.glucose,
            quality: (m as any).quality,
          })),
      )

      // By sensor: one trend chart per ticked sensor, oldest wear first, each
      // through the same chart pipeline as the summary series. A custom range
      // (or no selection) leaves this unset: one chart of the whole selection.
      if (selection.mode === 'devices' && selection.selectedRows.length > 0) {
        vetExporter.setTrendSeries(
          selection.selectedRows
            .filter((r) => r.first && r.last)
            .map((r) => ({
              title: formatTrendSeriesTitle(r.name, r.first as Date, r.last as Date, r.days),
              name: r.name,
              start: r.first as Date,
              end: r.last as Date,
              // The row cap cut the selection's oldest readings: a sensor that
              // was worn before the cut is clipped — say so on ITS chart, not
              // only in the page-1 PARTIAL marker (eng-brain P69-6).
              clippedBefore:
                truncatedBefore && (r.first as Date).getTime() < truncatedBefore.getTime()
                  ? truncatedBefore
                  : undefined,
              metrics: prepareGlucoseChartSeries(perDeviceData.get(String(r.id)) ?? [])
                .filter((m) => typeof m.timestamp === 'number')
                .map((m) => ({
                  timestamp: m.timestamp as number,
                  glucose: m.glucose,
                  quality: (m as any).quality,
                })),
            })),
        )
      }

      vetExporter.setActivities(
        activities.map((a: any) => ({
          id: a.id,
          type: a.type,
          start_time: a.start_time,
          description: a.description,
        })),
      )

      vetExporter.setEmrNotes(toReportEmrNotes(emrNotes))

      if (glucoseStats) {
        vetExporter.setGlucoseStats(glucoseStats)
      }

      // Get device name if available
      let deviceName: string | undefined
      try {
        const deviceIdStr = String(deviceId)
        if (deviceIdStr && !deviceIdStr.startsWith('virtual-')) {
          // Could fetch device name here if needed
        }
      } catch (e) {
        // Device name is optional
      }

      await vetExporter.exportToPDF({
        petInfo: {
          id: pet.id.toString(),
          name: pet.name,
          species: pet.species,
          breed: pet.breed,
          weight: pet.weight,
        },
        startDate: effectiveStartDate,
        endDate: effectiveEndDate,
        glucoseUnit,
        deviceName,
        devices: selection.selectedRows.map((r) => ({
          name: r.name,
          start: r.first as Date,
          end: r.last as Date,
        })),
        targetMin,
        targetMax,
        highMax,
        // Carry the server clamp into the document. The dialog's <Alert> warned
        // the vet who GENERATED the report; this is what tells whoever RECEIVES
        // it that the window is short (header marker + `-partial` filename).
        truncated: isTruncated,
      })

      // The report exists and has been handed to the browser — record that a
      // clinical record left the portal. Fire and forget; an audit failure must
      // never turn a produced report into a failed export (see recordExportEvent).
      trackNow({
        type: 'report_exported',
        section: 'report_dialog',
        detail: 'pdf',
        pet: Number(petId),
      })
      recordExportEvent({
        petId,
        deviceId:
          selection.selectedIds.length === 1 ? selection.selectedIds[0] : selection.queryDeviceId,
        devices: selection.queryDeviceIds,
        selectionMode: selection.selectionMode ?? undefined,
        format: 'pdf',
        unit: glucoseUnit,
        rangeStart: effectiveStartDate,
        rangeEnd: effectiveEndDate,
        rowCount: correctedMetrics.length,
        // RAW metrics, not correctedMetrics. The mask is a property of the
        // STORED reading (a sensor drop-out), not of the rendered series:
        // prepareGlucoseChartSeries has already BGM-corrected — so
        // countMaskedReadings would correct a SECOND time — clamped the floor
        // onto 2.2, and smoothed, and a 5-point trailing average blends an
        // isolated drop-out into its neighbours until it no longer trips the
        // bound. Measured on a 60-reading window with 4 drop-outs: raw counts
        // 4, the chart series counts 1. rowCount stays correctedMetrics.length
        // because that IS what the PDF renders.
        maskedCount: countMaskedReadings(metrics, glucoseUnit),
        truncated: isTruncated,
      })

      setExportStatus('complete')

      // Reset status after a brief delay
      setTimeout(() => {
        setExportStatus('idle')
      }, 2000)
    } catch (error) {
      console.error('Error exporting PDF:', error)
      setExportStatus('idle')
    } finally {
      setIsExporting(false)
    }
  }, [
    pet,
    activities,
    emrNotes,
    glucoseStats,
    metrics,
    effectiveStartDate,
    effectiveEndDate,
    glucoseUnit,
    targetMin,
    targetMax,
    highMax,
    isExporting,
    deviceId,
    selection.selectedRows,
    selection.selectedIds,
    selection.mode,
    perDeviceData,
    truncatedBefore,
    selection.queryDeviceId,
    selection.queryDeviceIds,
    selection.selectionMode,
  ])

  // Handle date picker confirmation - proceed with export
  // Close both dialogs and run the export
  const runExport = useCallback(() => {
    setEmptyConfirmOpen(false)
    setDatePickerOpen(false)
    // Trigger export after dialog closes
    setTimeout(() => {
      handleExportInternal()
    }, 100)
  }, [handleExportInternal])

  // The report is built from the readings, activities and notes of the window
  // the dialog has chosen. Their query keys follow the selection and keep no
  // previous data, so each reads [] until it lands: the preselect (or a tick)
  // re-keys them while the button would already be enabled, and a click in that
  // window produced a report with no readings and no notes. Wait for all three,
  // and refuse on a failed load the way the CSV does (a report silently missing
  // its notes is a wrong record, not a smaller one). TLA+ survey round 7.
  const reportDataLoading = metricsLoading || activitiesLoading || emrNotesLoading
  const reportDataError = metricsError ?? activitiesError ?? emrNotesError ?? null

  // By-device mode needs at least one ticked sensor; never fall back silently.
  const hasDeviceRows = selection.rows.some((r) => r.first && r.last)
  const canConfirm =
    !selection.rowsLoading &&
    !reportDataLoading &&
    !reportDataError &&
    !(selection.mode === 'devices' && hasDeviceRows && selection.selectedIds.length === 0)

  const handleDatePickerConfirm = useCallback(() => {
    // "Still loading" is not "no data". The confirm button is disabled while
    // the range query is in flight, so this is belt-and-braces — but the guard
    // below MUST NOT fire on an unresolved query, or it accuses a pet with
    // thousands of readings of having none.
    if (dataRangeLoading || !canConfirm) return
    // Guard against silently generating an empty (all-zero) report: if the
    // selected range has no readings, ask the user to confirm or pick the
    // data range instead.
    if (!selectedRangeHasData) {
      setEmptyConfirmOpen(true)
      return
    }
    runExport()
  }, [dataRangeLoading, canConfirm, selectedRangeHasData, runExport])

  const getStatusText = () => {
    switch (exportStatus) {
      case 'preparing':
        return translate('pos.pdf.preparing')
      case 'generating':
        return translate('pos.pdf.generating')
      case 'complete':
        return translate('pos.pdf.complete')
      default:
        return translate('pos.pdf.generate_report')
    }
  }

  // Part 8: Date Range Picker Dialog Component
  const datePickerDialog = (
    <Dialog open={datePickerOpen} onClose={handleDatePickerCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DateRangeIcon />
        {translate('pos.pdf.select_date_range')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {reportDataError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {translate('pos.pdf.report_load_error')}
            </Alert>
          )}
          {isTruncated && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {translate('pos.reports.truncated_warning')}
            </Alert>
          )}
          <ReportDeviceSelector
            rows={selection.rows}
            isLoading={selection.rowsLoading}
            error={selection.rowsError}
            mode={selection.mode}
            onModeChange={selection.setMode}
            selectedIds={selection.selectedIds}
            onSelectedIdsChange={selection.setSelectedIds}
            customStart={selection.customStart}
            customEnd={selection.customEnd}
            onCustomRangeChange={selection.setCustomRange}
            dataRange={dataRange}
            showPresets
          />

          {/* Selected Range Preview */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {translate('pos.export_selection.report_window')}:
            </Typography>
            <Typography variant="subtitle1" fontWeight="bold">
              {format(effectiveStartDate, 'MMM d, yyyy')} —{' '}
              {format(effectiveEndDate, 'MMM d, yyyy')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {/* Pluralized ("1 day", not "1 days"). */}
              {translate('pos.export_selection.days_worn', {
                smart_count: Math.max(
                  1,
                  Math.ceil(
                    (effectiveEndDate.getTime() - effectiveStartDate.getTime()) /
                      (24 * 60 * 60 * 1000),
                  ),
                ),
              })}
            </Typography>
          </Box>

          {/* Warning when no data available for selected range */}
          {!selectedRangeHasData && dataRange && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: 'warning.light',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'warning.main',
              }}
            >
              <Typography variant="body2" color="warning.dark" fontWeight="bold" sx={{ mb: 1 }}>
                {translate('pos.pdf.no_data_warning')}
              </Typography>
              <Typography variant="body2" color="warning.dark" sx={{ mb: 1 }}>
                {translate('pos.pdf.actual_data_range')}:{' '}
                {format(dataRange.startDate, 'MMM d, yyyy')} —{' '}
                {format(dataRange.endDate, 'MMM d, yyyy')}
              </Typography>
              <Chip
                label={translate('pos.pdf.use_actual_range')}
                onClick={applyActualDataRange}
                color="warning"
                size="small"
                sx={{ mt: 1, cursor: 'pointer' }}
              />
            </Box>
          )}

          {/* A custom range covers every sensor of the pet */}
          {selection.mode === 'custom' && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
              <Typography variant="caption" color="primary.contrastText">
                {translate('pos.pdf.pet_aggregated_notice')}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDatePickerCancel}>{translate('ra.action.cancel')}</Button>
        <Button
          onClick={handleDatePickerConfirm}
          variant="contained"
          color="primary"
          disabled={dataRangeLoading || !canConfirm}
          startIcon={
            dataRangeLoading || reportDataLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <PictureAsPdf />
            )
          }
        >
          {dataRangeLoading || reportDataLoading
            ? translate('pos.pdf.checking_data')
            : translate('pos.pdf.generate_report')}
        </Button>
      </DialogActions>
    </Dialog>
  )

  // Confirmation shown when generating over a range that has no readings.
  const emptyRangeConfirmDialog = (
    <Dialog
      open={emptyConfirmOpen}
      onClose={() => setEmptyConfirmOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{translate('pos.pdf.empty_confirm_title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: dataRange ? 1 : 0 }}>
          {translate(dataRange ? 'pos.pdf.empty_confirm_message' : 'pos.pdf.empty_confirm_no_data')}
        </Typography>
        {dataRange && (
          <Typography variant="body2" color="text.secondary">
            {translate('pos.pdf.actual_data_range')}: {format(dataRange.startDate, 'MMM d, yyyy')} —{' '}
            {format(dataRange.endDate, 'MMM d, yyyy')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEmptyConfirmOpen(false)}>{translate('ra.action.cancel')}</Button>
        {dataRange && (
          <Button
            onClick={() => {
              applyActualDataRange()
              setEmptyConfirmOpen(false)
            }}
            variant="contained"
            color="primary"
          >
            {translate('pos.pdf.use_actual_range')}
          </Button>
        )}
        <Button onClick={runExport} color="inherit" disabled={!canConfirm}>
          {translate('pos.pdf.generate_anyway')}
        </Button>
      </DialogActions>
    </Dialog>
  )

  if (variant === 'fab') {
    return (
      <>
        {datePickerDialog}
        {emptyRangeConfirmDialog}
        <Button
          variant="contained"
          color="primary"
          startIcon={isExporting ? <CircularProgress size={20} /> : <PictureAsPdf />}
          onClick={handleOpenDatePicker}
          disabled={isExporting || !pet}
          size={size}
          sx={{ minWidth: 160 }}
        >
          {getStatusText()}
        </Button>
      </>
    )
  }

  return (
    <>
      {datePickerDialog}
      {emptyRangeConfirmDialog}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={isExporting ? <CircularProgress size={20} /> : <PictureAsPdf />}
          onClick={handleOpenDatePicker}
          disabled={isExporting || !pet}
          size={size}
          sx={{ minWidth: 160 }}
        >
          {getStatusText()}
        </Button>

        {exportStatus !== 'idle' && exportStatus !== 'complete' && (
          <Typography variant="caption" color="text.secondary">
            {translate(`pos.pdf.status_description.${exportStatus}`)}
          </Typography>
        )}
      </Box>
    </>
  )
}
